import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import * as XLSX from 'xlsx';
import { fetchMeals, fetchBagging } from "../data/api.js";
import { generateMenu } from '../utils/generateMenu';
// import { PDFDownloadLink, Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, BorderStyle, WidthType, CheckBox } from 'docx';
import { saveAs } from 'file-saver';
import ShoppingPrepHome from './ShoppingPrepHome';
import ShoppingPrepList from './ShoppingPrepList';
import PopularityChart from './PopularityChart.jsx';

const MealPlanner = () => {
    const [homes, setHomes] = useState({});
    const [meals, setMeals] = useState([]);
    const [bagging, setBagging] = useState({});
    const [activeTab, setActiveTab] = useState('mealRanking');
    const [loading, setLoading] = useState(true);
    const [xlsxFile, setXlsxFile] = useState(null);
    const [currentHomeIndex, setCurrentHomeIndex] = useState(0);
    const [mealRankings, setMealRankings] = useState({
        breakfast: [],
        lunch: [],
        dinner: [],
    });
    const [homePreferences, setHomePreferences] = useState({});
    const [mealServings, setMealServings] = useState({}); // { [mealId]: servingSize }
    const [mealCounts, setMealCounts] = useState({});
    const [mealOccurances, setMealOccurances] = useState({});
    const [generatedMenus, setGeneratedMenus] = useState({
      breakfast: null,
      lunch: null,
      dinner: null
    });

    // const []

    const homeIds = Object.keys(homes);
    const totalHomes = homeIds.length;
    const currentHomeId = homeIds[currentHomeIndex];
    const currentHome = homes[currentHomeId];
    const residents = currentHome?.residents || 1;

    const generateMenuDocx = async (homeId, home, menus, bagging) => {
      // Defensive programming to prevent crashes
      if (!homeId || !home || !menus) {
        throw new Error('Missing data required for DOCX generation');
      }
      
      const doc = new Document({
        sections: [
          {
            properties: {},
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: `${homeId} Menu, ${home.residents || 0} residents`,
                    bold: true,
                  }),
                ],
                heading: 'Heading1',
                alignment: 'center',
              }), 

              new Paragraph({
                children: [
                  new TextRun({
                    text: home.dietary_restrictions?.length > 0 
                    ? home.dietary_restrictions.join(', ') 
                    : '*no dietary restrictions*',
                    bold: true,
                  }),
                ],
                heading: 'Heading1',
                alignment: 'center',
              }), 
              
              // Meal times section
              new Paragraph({ text: "", spacing: { before: 400 }, verticalAlign: 'center', italics: false, }, ),

              new Paragraph({ text: "8am Breakfast", spacing: { before: 400 }, verticalAlign: 'center', italics: false, }, ),
              new Paragraph({ text: "10am Snack", verticalAlign: 'center', italics: false, }),
              new Paragraph({ text: "12pm Lunch", verticalAlign: 'center' }),
              new Paragraph({ text: "3pm Snack", verticalAlign: 'center' }),
              new Paragraph({ text: "5pm Dinner", verticalAlign: 'center' }),
              new Paragraph({ text: "7pm Snack", verticalAlign: 'center' }),
              
              new Paragraph({
                text: "Per Oregon Administrative Rule, people are not restricted to specific mealtimes",
                spacing: { before: 400, after: 400 }
              }),
              
              // Weekly menus
              ...generateWeeklyMenuSections(homeId, home, menus, bagging)
            ]
          }
        ]
      });
      
      return doc;
    };
    
    // Helper function to generate the weekly menu sections
    const generateWeeklyMenuSections = (homeId, home, menus, bagging) => {
      const sections = [];
      
      for (let weekIndex = 0; weekIndex < 4; weekIndex++) {
        sections.push(
          new Paragraph({
            text: `Week ${weekIndex + 1} Menu`,
            heading: 'Heading2',
            spacing: { before: 400, after: 200 }
          })
        );
        
        // Create weekly menu table
        const rows = [];
        
        // Header row
        rows.push(
          new TableRow({
            children: [
              new TableCell({ 
                children: [new Paragraph("Day")],
                width: { size: 10, type: WidthType.PERCENTAGE }
              }),
              new TableCell({ 
                children: [new Paragraph("Meal")],
                width: { size: 45, type: WidthType.PERCENTAGE }
              }),
              new TableCell({ 
                children: [new Paragraph("Calories")],
                width: { size: 15, type: WidthType.PERCENTAGE }
              }),
              new TableCell({ 
                children: [new Paragraph("Sodium")],
                width: { size: 11, type: WidthType.PERCENTAGE }
              }),
              new TableCell({ 
                children: [new Paragraph("Carbs")],
                width: { size: 11, type: WidthType.PERCENTAGE }
              }),
              new TableCell({ 
                children: [new Paragraph("Cook Time")],
                width: { size: 11, type: WidthType.PERCENTAGE }
              })
            ]
          })
        );
        
        // Day rows
        for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
          const dayNumber = weekIndex * 7 + dayIndex + 1;
          const breakfast = getMealForDay(menus.breakfast, dayNumber);
          const lunch = getMealForDay(menus.lunch, dayNumber);
          const dinner = getMealForDay(menus.dinner, dayNumber);
          
          // Create three rows for each day (breakfast, lunch, dinner)
          const meals = [
            { type: "Breakfast", meal: breakfast },
            { type: "Lunch", meal: lunch },
            { type: "Dinner", meal: dinner }
          ]
          
          meals.forEach((mealData, index) => {
            const cells = [];
            
            // Only add the day cell for the first row of each day
            if (index === 0) {
              cells.push(
                new TableCell({ 
                  children: [new Paragraph(`Day ${dayIndex + 1}`)],
                  rowSpan: 3,
                  verticalAlign: 'center'
                })
              );
            }
            
            // Add the other cells
            cells.push(
              new TableCell({ 
                children: [new Paragraph(mealData.meal?.mealName || '-')],
                verticalAlign: 'center'
              }),
              new TableCell({ 
                children: [new Paragraph(mealData.meal?.calories?.toString() || '-')],
                verticalAlign: 'center'
              }),
              new TableCell({ 
                children: [new Paragraph(mealData.meal?.sodium?.toString() || '-')],
                verticalAlign: 'center'
              }),
              new TableCell({ 
                children: [new Paragraph(mealData.meal?.carbs?.toString() || '-')],
                verticalAlign: 'center'
              }),
              new TableCell({ 
                children: [new Paragraph(mealData.meal?.prepTime?.toString() || '-')],
                verticalAlign: 'center'
              })
            );
            
            const tableRow = new TableRow({ children: cells });
            rows.push(tableRow);
          });
        }
        
        
        const weeklyTable = new Table({
          rows: rows,
          width: { size: 100, type: WidthType.PERCENTAGE },
          columnWidths: [800, 3000, 1300, 1300, 1300, 1300],
          alignment: 'center',
          borders: {
            insideHorizontal: {
              style: BorderStyle.SINGLE,
              size: 1,
            },
            insideVertical: {
              style: BorderStyle.SINGLE,
              size: 1,
            },
            top: {
              style: BorderStyle.SINGLE,
              size: 1,
            },
            bottom: {
              style: BorderStyle.SINGLE,
              size: 1,
            },
            left: {
              style: BorderStyle.SINGLE,
              size: 1,
            },
            right: {
              style: BorderStyle.SINGLE,
              size: 1,
            },
          }
        });
        
        sections.push(weeklyTable);
      }
        
        // Add bagging checklists
        // Add consolidated bagging section at the end
        sections.push(
          new Paragraph({
            text: 'Monthly Bagging Checklist',
            heading: 'Heading2',
            spacing: { before: 400, after: 200 }
          })
        );
        
        // Use mealOccurances data structure for bagging information
        const homeMealOccurances = mealOccurances[homeId] || {};
        
        // Process each meal that appears in the menu
        Object.entries(homeMealOccurances).forEach(([mealId, servingSizeCounts]) => {
          const meal = meals.find(m => m.id === mealId);
          if (!meal) return;

          sections.push(
            new Paragraph({
              text: meal.name,
              heading: 'Heading4',
              spacing: { before: 200, after: 100 }
            })
          );

          // Process each serving size for this meal
          Object.entries(servingSizeCounts).forEach(([servingSize, count]) => {
            const bagItems = bagging[mealId]?.[servingSize];
            if (!bagItems) return;

            bagItems.forEach(item => {
              sections.push(
                new Paragraph({
                  children: [
                    new CheckBox({ checked: false }),
                    new TextRun({ 
                      text: `  ${item} × ${count}`,
                      size: 20 
                    })
                  ],
                  spacing: { after: 60 }
                })
              );
            });
          });
        });
      
      
      return sections;
    };
    
    // Helper function to get meal for a specific day
    const getMealForDay = (meals, day) => {
      if (!Array.isArray(meals)) return null;
      return meals.find(meal => meal.day === day) || null;
    };
    
    // Function to get bagging items for a meal
    const getBaggingItems = (meal) => {
      if (!meal) return [];
      
      const homeOccurances = mealOccurances[currentHomeId] || {};
      const mealOccurance = homeOccurances[meal.mealId] || {};
      
      // Get the bagging info for this meal
      const bagItems = bagging[meal.mealId];
      if (!bagItems) return [];

      // If bagging has nested structure with serving sizes
      if (typeof bagItems === 'object') {
        const servingSize = meal.servings;
        
        // Look for exact match first
        if (bagItems[servingSize]) {
          return bagItems[servingSize].map(item => {
            const count = mealOccurance[servingSize] || 1;
            return `${item} (x${count})`;
          });
        }
      }
      
      return [];
    };

    const handlePrevHome = () => {
        setCurrentHomeIndex((prev) => (prev > 0 ? prev - 1 : totalHomes - 1));
    };

    const handleNextHome = () => {
        setCurrentHomeIndex((prev) => (prev < totalHomes - 1 ? prev + 1 : 0));
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const mealsResponse = await fetchMeals();
                const baggingData = await fetchBagging();
                // const instructionsResponse = fetchInstructions();

                setMeals([
                    ...mealsResponse.breakfast,
                    ...mealsResponse.lunch,
                    ...mealsResponse.dinner
                ]);
                setBagging(baggingData);
                
            } catch (error) {
                console.error('Error loading data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        if (Object.keys(homes).length > 0) {
            const initialMenus = {};
            Object.keys(homes).forEach(homeId => {
            initialMenus[homeId] = {
                breakfast: null,
                lunch: null,
                dinner: null
            };
            });
            setGeneratedMenus(initialMenus);
        }
    }, [homes]);


    const transformXlsxData = (jsonData) => {
        const homes = {};
        jsonData.forEach(row => {
            const homeId = row['home_id']; // Adjust if your header is different
            homes[homeId] = {
                phone: row['phone'] || '',
                email: row['email'] || '',
                residents: parseInt(row['residents'] || 0, 10) || 0,
                dietary_restrictions: row['dietary_restrictions'] ? String(row['dietary_restrictions']).split(',') : [],
                breakfast_preferences: row['breakfast_preferences'] ? String(row['breakfast_preferences']).split(/,\s*/) : [],
                lunch_preferences: row['lunch_preferences'] ? String(row['lunch_preferences']).split(/,\s*/) : [],
                dinner_preferences: row['dinner_preferences'] ? String(row['dinner_preferences']).split(/,\s*/) : [],
            };
        });
        console.log(homes)
        return homes;
    };

    const parseXlsx = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const sheetName = workbook.SheetNames[0];  // Assuming first sheet
                    const worksheet = workbook.Sheets[sheetName];
                    const jsonData = XLSX.utils.sheet_to_json(worksheet);
                    resolve(jsonData);
                } catch (err) {
                    reject(err);
                }
            };
            reader.onerror = (err) => reject(err);
            reader.readAsArrayBuffer(file);
        });
    };

    const compileMealPopularity = (homesData) => {
        const allBreakfasts = Object.values(homesData).flatMap(home => home.breakfast_preferences);
        const allLunches = Object.values(homesData).flatMap(home => home.lunch_preferences);
        const allDinners = Object.values(homesData).flatMap(home => home.dinner_preferences);

        // Calculate meal counts
        const counts = {};
        [...allBreakfasts, ...allLunches, ...allDinners].forEach(mealId => {
            if (mealId) {
                counts[mealId] = (counts[mealId] || 0) + 1;
            }
        });
        setMealCounts(counts);

        const rankMeals = (mealPreferences) => {
          const mealCounts = {};
          mealPreferences.forEach(meal => {
              mealCounts[meal] = (mealCounts[meal] || 0) + 1;
          });

            // Sort meals by popularity (descending) and then alphabetically by mealId
            const sortedMeals = Object.entries(mealCounts).sort((
              [mealIdA, countA],
              [mealIdB, countB]
            ) => {
                if (countB !== countA) {
                    return countB - countA; // Sort by count first
                }
                return mealIdA.localeCompare(mealIdB); // Then sort alphabetically
            });

            const rankedMeals = [];
            let currentRank = 1;
            let previousCount = null;

            sortedMeals.forEach(([mealId, count]) => {
              if (previousCount !== null && count < previousCount) {
                  currentRank++; // Increment rank by 1 when count changes
              }
              rankedMeals.push({ mealId, rank: currentRank });
              previousCount = count;
          });

            // Return the rankedMeals as an array of mealIds, preserving the order
            return rankedMeals.map(({ mealId }) => mealId);
        };

        const breakfastRankings = rankMeals(allBreakfasts);
        const lunchRankings = rankMeals(allLunches);
        const dinnerRankings = rankMeals(allDinners);

        setMealRankings({
            breakfast: breakfastRankings,
            lunch: lunchRankings,
            dinner: dinnerRankings,
        });

        const initialHomePreferences = {};
        for (const homeId in homesData) {
            initialHomePreferences[homeId] = {
                breakfast: homesData[homeId].breakfast_preferences.sort((a, b) => {
                    const aCount = allBreakfasts.filter(id => id === a).length;
                    const bCount = allBreakfasts.filter(id => id === b).length;
                    return bCount - aCount;
                }),
                lunch: homesData[homeId].lunch_preferences.sort((a, b) => {
                    const aCount = allLunches.filter(id => id === a).length;
                    const bCount = allLunches.filter(id => id === b).length;
                    return bCount - aCount;
                }),
                dinner: homesData[homeId].dinner_preferences.sort((a, b) => {
                    const aCount = allDinners.filter(id => id === a).length;
                    const bCount = allDinners.filter(id => id === b).length;
                    return bCount - aCount;
                }),
            };
        }
        setHomePreferences(initialHomePreferences);

        // Initialize meal servings state
        const initialMealServings = {};
        for (const mealType of ['breakfast', 'lunch', 'dinner']) {
            const rankings = mealRankings[mealType];
            if (rankings) {
                rankings.forEach(mealId => {
                    initialMealServings[mealId] = optimizeServingsForMeal(mealId, 3);
                });
            }
        }
        setMealServings(initialMealServings);

    };

    const handleFileChange = (event) => {
        setXlsxFile(event.target.files[0]);
    };

    const handleUpload = async () => {
        if (!xlsxFile) {
            alert('Please select an XLSX file.');
            return;
        }

        setLoading(true);
        try {
            const jsonData = await parseXlsx(xlsxFile);
            const transformedHomes = transformXlsxData(jsonData);
            setHomes(transformedHomes);
            const mealsData = {
                breakfast: meals.filter(meal => meal.type === 'breakfast'),
                lunch: meals.filter(meal => meal.type === 'lunch'),
                dinner: meals.filter(meal => meal.type === 'dinner'),
            };
            compileMealPopularity(transformedHomes, mealsData);
            
        } catch (error) {
            console.error('Error parsing XLSX file:', error);
            alert('Error parsing XLSX file. Check file format and headers.');
        } finally {
            setLoading(false);
        }
    };

    const handleTabClick = (tab) => {
        setActiveTab(tab);
    };

    const onDragEnd = (result, mealType) => {
        if (!result.destination) return;

        const { source, destination } = result;
        if (source.droppableId !== destination.droppableId) {
            console.warn('Drag and drop between different droppables is not allowed. The code must be altered to do that');
            return;
        }

        const items = Array.from(homePreferences[currentHomeId][mealType]);
        const [reorderedItem] = items.splice(source.index, 1);
        items.splice(destination.index, 0, reorderedItem);

        setHomePreferences((prevPreferences) => ({
            ...prevPreferences,
            [currentHomeId]: {
                ...prevPreferences[currentHomeId],
                [mealType]: items,
            },
        }));
    };

    const handleServingChange = (mealId, value) => {
        setMealServings((prev) => ({ ...prev, [mealId]: parseInt(value) }));
    };

    const optimizeServingsForMeal = () => {
        return 1; // Default serving size
    };

    const getMealRanking = (mealId, mealType) => {
      const mealRankingArray = mealRankings[mealType];
      if (!mealRankingArray) {
          return 'N/A';
      }

      // Find the index of the meal in the ranked array
      const index = mealRankingArray.findIndex(id => id === mealId);
      if (index === -1) return 'N/A';

      // Get all meals with their counts for determining rank
      const mealPreferences = Object.values(homes).flatMap(home => home[`${mealType}_preferences`]);
      const mealCounts = {};
      mealPreferences.forEach(meal => {
          mealCounts[meal] = (mealCounts[meal] || 0) + 1;
      });

      // Get unique counts in descending order
      const uniqueCounts = [...new Set(Object.values(mealCounts))].sort((a, b) => b - a);
      
      // Get the count for the current meal
      const currentCount = mealCounts[mealId] || 0;
      
      // Rank is the index of the current count in uniqueCounts + 1
      const rank = uniqueCounts.indexOf(currentCount) + 1;

      return rank.toString();
  };  

  // New function to generate all menus at once
  const handleGenerateAllMenus = () => {
    const newMenus = {};
    const currentHomeOccurances = {};

    // Reset mealOccurances for the current home
    setMealOccurances(prev => ({
      ...prev,
      [currentHomeId]: {}
    }));

    ['breakfast', 'lunch', 'dinner'].forEach(mealType => {
      const preferences = homePreferences[currentHomeId]?.[mealType] || [];
      const { menu, mealOccurances: updatedOccurances } = generateMenu(
        preferences, 
        mealServings, 
        residents, 
        meals, 
        {}  // Pass empty object since we're resetting occurrences
      );

      newMenus[mealType] = menu;
      
      // Merge the updated occurrences for this meal type
      Object.entries(updatedOccurances).forEach(([mealId, servingSizeCounts]) => {
        if (!currentHomeOccurances[mealId]) {
          currentHomeOccurances[mealId] = {};
        }
        currentHomeOccurances[mealId] = {
          ...currentHomeOccurances[mealId],
          ...servingSizeCounts
        };
      });
    });

    // Update states with new data
    setMealOccurances(prev => ({
      ...prev,
      [currentHomeId]: currentHomeOccurances
    }));

    setGeneratedMenus(prevMenus => ({
      ...prevMenus,
      [currentHomeId]: newMenus
    }));
  };

  const reorganizeToAvoidDuplicates = (menuArray) => {
    if (!menuArray || menuArray.length <= 1) return menuArray;
    
    const result = [...menuArray];
    
    for (let i = 1; i < result.length; i++) {
      if (result[i].mealName === result[i-1].mealName) {
        let swapIndex = -1;
        for (let j = i + 1; j < result.length; j++) {
          if (result[j].mealName !== result[i-1].mealName && 
              (j === result.length - 1 || result[j].mealName !== result[j+1].mealName) &&
              (i === result.length - 1 || result[j].mealName !== result[i+1].mealName)) {
            swapIndex = j;
            break;
          }
        }
        
        if (swapIndex !== -1) {
          [result[i], result[swapIndex]] = [result[swapIndex], result[i]];
        }
      }
    }
    
    result.forEach((item, index) => {
      item.day = index + 1;
    });
    
    return result;
  };

  const renderMealTypeMenu = (mealType) => {
    const homeMenus = generatedMenus[currentHomeId];
    if (!homeMenus) return null;
    
    const menu = homeMenus[mealType];
    if (!menu) return null;

    const handleReorganizeMealType = () => {
        const reorganizedMenu = reorganizeToAvoidDuplicates(menu);
        setGeneratedMenus(prev => ({
          ...prev,
          [currentHomeId]: {
            ...prev[currentHomeId],
            [mealType]: reorganizedMenu
          }
        }));
      };

    // Split the menu into weeks (7 days each)
    const weeks = Array.from({ length: 4 }, (_, weekIndex) => 
      menu.slice(weekIndex * 7, (weekIndex + 1) * 7)
    );

    const onDragEndMenu = (result) => {
        if (!result.destination) return;
    
        const { source, destination } = result;
        const sourceWeek = parseInt(source.droppableId);
        const destWeek = parseInt(destination.droppableId);
        
        const newGeneratedMenus = { ...generatedMenus };
        const updatedMenu = [...menu];
        
        const sourceIndex = sourceWeek * 7 + source.index;
        const destIndex = destWeek * 7 + destination.index;
        
        const [movedItem] = updatedMenu.splice(sourceIndex, 1);
        updatedMenu.splice(destIndex, 0, movedItem);
        
        updatedMenu.forEach((item, index) => {
          item.day = index + 1;
        });
        
        newGeneratedMenus[currentHomeId] = {
          ...newGeneratedMenus[currentHomeId],
          [mealType]: updatedMenu
        };
        setGeneratedMenus(newGeneratedMenus);
      };

    return (
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-xl font-semibold capitalize">{mealType} Menu</h3>
          <button
            onClick={handleReorganizeMealType}
            className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-colors"
          >
            Reorganize to Avoid Duplicates
          </button>
        </div>
        <DragDropContext onDragEnd={onDragEndMenu}>
          <div className="grid grid-cols-4 gap-4">
            {weeks.map((week, weekIndex) => (
              <div key={weekIndex} className="bg-white rounded shadow">
                <div className="bg-gray-100 px-2 py-1 text-sm font-medium border-b">
                  Week {weekIndex + 1}
                </div>
                <Droppable droppableId={weekIndex.toString()}>
                  {(provided) => (
                    <ul 
                      className="text-sm min-h-[200px]"
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      {week.map((item, itemIndex) => (
                        <Draggable
                          key={`${item.day}-${item.mealType}`}
                          draggableId={`${item.day}-${item.mealType}`}
                          index={itemIndex}
                        >
                          {(provided) => (
                            <li
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className="px-2 py-1 border-b last:border-b-0 hover:bg-gray-50 cursor-move"
                            >
                              <div className="flex justify-between items-center">
                                <span className="font-medium">Day {item.day}</span>
                                <span className="text-xs text-gray-500">
                                  {residents}/{item.servings}
                                </span>
                              </div>
                              <div className="truncate">
                                {item.mealName === "Use Leftovers" ? (
                                  <span className="text-blue-600">Use Leftovers</span>
                                ) : (
                                  <span>{item.mealName}</span>
                                )}
                              </div>
                            </li>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </ul>
                  )}
                </Droppable>
              </div>
            ))}
          </div>
        </DragDropContext>
      </div>
      
    );
    }

  // Modified renderTabContent
  const renderTabContent = () => {
    if (loading) {
      return <p className="text-center text-gray-500">Loading...</p>;
    }

    if (totalHomes === 0) {
      return <p className="text-center text-gray-500">No homes available.</p>;
    }

    switch (activeTab) {
      case 'mealRanking':
        return (
            <div className="p-2">
            {/* Header info */}
            <div className="mb-2 bg-white rounded shadow p-2">
              <div className="grid grid-cols-3 gap-2 items-center text-sm">
                <div>Home: <span className="font-semibold">{currentHomeId}</span></div>
                <div>Residents: <span className="font-semibold">{residents}</span></div>
                <div>
                  Restrictions: <span className="font-semibold">
                    {homes[currentHomeId].dietary_restrictions?.length ? 
                      homes[currentHomeId].dietary_restrictions.join(', ') : 'None'}
                  </span>
                </div>
              </div>
            </div>
                
            {/* Meal rankings grid */}
            <div className="grid grid-cols-3 gap-4">
              {['breakfast', 'lunch', 'dinner'].map((mealType) => (
                <div key={mealType} className="bg-white rounded shadow">
                  <h4 className="text-base font-semibold capitalize bg-gray-100 p-2">{mealType} Rankings</h4>
                  <div className="p-2">
                    <DragDropContext onDragEnd={(result) => onDragEnd(result, mealType)}>
                      <Droppable droppableId={currentHomeId.toString()}>
                        {(provided) => (
                          <ul className="list-none p-0 space-y-1 text-base"
                            {...provided.droppableProps}
                            ref={provided.innerRef}>
                            {homePreferences[currentHomeId]?.[mealType]?.map((mealId, index) => {
                              const ranking = getMealRanking(mealId, mealType);
                              const meal = meals.find((m) => m.id === mealId);
                              const servingsOptions = meal?.servings || [];

                              return (
                                <Draggable
                                  key={mealId.toString()}
                                  draggableId={mealId.toString()}
                                  index={index}>
                                  {(provided) => (
                                    <li
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      className="border rounded px-2 py-1 bg-gray-50 cursor-move text-sm">
                                      <div className="flex items-center justify-between">
                                        <span className="text-green-500 font-medium">#{ranking}</span>
                                        <span className="truncate mx-2 flex-grow">{mealId}</span>
                                        {servingsOptions.length > 1 ? (
                                          <select
                                            value={mealServings[mealId] || 1}
                                            onChange={(e) => handleServingChange(mealId, e.target.value)}
                                            className="border rounded text-sm p-0">
                                            {servingsOptions.map((option) => (
                                              <option key={option} value={option}>
                                                {option}
                                              </option>
                                            ))}
                                          </select>
                                        ) : (
                                          <span>{servingsOptions[0] || 1}</span>
                                        )}
                                      </div>
                                    </li>
                                  )}
                                </Draggable>
                              );
                            })}
                            {provided.placeholder}
                          </ul>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </div>
                </div>
              ))}
              
              <button
                onClick={handleGenerateAllMenus}
                className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-lg shadow-md transition duration-200 ease-in-out">
                Generate Home Menu
              </button>
            </div>

            {/* Render each meal type menu separately */}
            <div className="mt-8">
              {renderMealTypeMenu('breakfast')}
              {renderMealTypeMenu('lunch')}
              {renderMealTypeMenu('dinner')}
            
            </div>
            {generatedMenus[currentHomeId] && Object.values(generatedMenus[currentHomeId]).every(menu => Array.isArray(menu)) && (
              <div className="mt-4 flex space-x-4">
                {/* Send to Drive button */}
                <button
                  onClick={async () => {
                    try {
                      const doc = await generateMenuDocx(
                        currentHomeId,
                        homes[currentHomeId] || {},
                        generatedMenus[currentHomeId] || {},
                        bagging
                      );
                      
                      // Generate blob and send to webhook
                      const blob = await Packer.toBlob(doc);
                      const formData = new FormData();
                      formData.append('file', blob, `menu-plan-${currentHomeId}.docx`);

                      const response = await fetch('https://hook.us2.make.com/iwk9zo0845y9d922bh6qsenrd2voyp2w', {
                        method: 'POST',
                        body: formData
                      });

                      if (!response.ok) {
                        throw new Error('Failed to send file to Drive');
                      }

                      alert('Menu successfully sent to Drive!');
                    } catch (error) {
                      console.error("Error sending to Drive:", error);
                      alert("Error sending menu to Drive. Please try again.");
                    }
                  }}
                  className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-lg shadow-md transition duration-200 ease-in-out"
                >
                  Send to Drive
                </button>
                {/* Replace the PDFDownloadLink with this button */}
                <button
                  onClick={async () => {
                    try {
                      const doc = await generateMenuDocx(
                        currentHomeId,
                        homes[currentHomeId] || {},
                        generatedMenus[currentHomeId] || {},
                        bagging
                      );
                      
                      // Generate and save the file
                      Packer.toBlob(doc).then(blob => {
                        saveAs(blob, `menu-plan-${currentHomeId}.docx`);
                      });
                    } catch (error) {
                      console.error("Error generating DOCX:", error);
                      alert("Error generating DOCX document. Please try again.");
                    }
                  }}
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-6 rounded-lg shadow-md transition duration-200 ease-in-out"
                >
                  Download Menu DOCX
                </button>

                {/* // Save the current menu to localStorage as a backup */}
                {/* <button
                  onClick={() => {
                    localStorage.setItem(
                      `menu-${currentHomeId}`, 
                      JSON.stringify(generatedMenus[currentHomeId])
                    );
                    alert(`Menu for ${currentHomeId} saved as backup`);
                  }}
                  className="bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-lg shadow-md transition duration-200 ease-in-out"
                  >
                    Save Menu as Backup
                  </button> */}
                </div>
              )}
            </div>
          );
        case 'popularity':
          return (
            <PopularityChart
              mealOccurances={mealOccurances}
              homes={homes}
            />
          );
        case 'masterShoppingPrep':
          return (
            <ShoppingPrepList
              currentHomeId={currentHomeId}
              mealOccurances={mealOccurances}
              meals={meals}
              bagging={bagging}
          />
        );
        case 'homeShoppingPrep':
          return (
            <ShoppingPrepHome
              currentHomeId={currentHomeId}
              mealOccurances={mealOccurances}
              meals={meals}
              bagging={bagging}
            />
          );

      default:
        return null;
      }
      
    };
  


  return (
        <div className="container mx-auto px-4 py-8">
            {loading ? (
                <div className="text-center">
                    <p>Loading...</p>
                </div>
            ) : (
                <div>
                    <div className="mb-4">
                        <input
                            type="file"
                            accept=".xlsx"
                            onChange={handleFileChange}
                            className="mr-2"
                        />
                        <button
                            onClick={handleUpload}
                            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                        >
                            Upload XLSX
                        </button>
                    </div>

                    {totalHomes > 0 && (
                    <div className="flex items-center justify-between mb-6">
                        <button
                            onClick={handlePrevHome}
                            className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                        >
                            ← Previous Home
                        </button>
                        <div className="text-center">
                            <span className="font-medium">
                                Home {currentHomeIndex + 1} of {totalHomes}
                            </span>
                        </div>
                        <button
                            onClick={handleNextHome}
                            className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                        >
                            Next Home →
                        </button>
                    </div>
                )}

                    <div className="mb-4">
                        <button
                          className={`mr-4 px-4 py-2 ${activeTab === 'mealRanking' ? 'bg-blue-500 text-white' : 'bg-gray-200'} rounded`}
                          onClick={() => handleTabClick('mealRanking')}
                        >
                          Meal Rankings
                        </button>
                        <button
                          onClick={() => setActiveTab('popularity')}
                          className={`mr-4 px-4 py-2 ${activeTab === 'popularity' ? 'bg-blue-500 text-white' : 'bg-gray-200'} rounded`}
                        >
                          Meal Popularity
                        </button>
                        <button
                          className={`mr-6 px-4 py-2 ${activeTab === 'homeShoppingPrep' ? 'bg-blue-500 text-white' : 'bg-gray-200'} rounded`}
                          onClick={() => handleTabClick('homeShoppingPrep')}
                        >
                          Home Shopping & Prep
                        </button>
                        <button
                          className={`px-4 py-2 ${activeTab === 'masterShoppingPrep' ? 'bg-blue-500 text-white' : 'bg-gray-200'} rounded`}
                          onClick={() => handleTabClick('masterShoppingPrep')}
                        >
                          Master Shopping & Prep
                        </button>
                    </div>

                    {renderTabContent()}
                </div>
            )}
        </div>
    );
};

export default MealPlanner;